import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { Badge, Button } from '@repo/shared-client/components/ui'
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import type { FollowingCountsQuery, FollowingCountsQueryVariables } from 'types/graphql'

export const FOLLOWING_COUNTS_QUERY: TypedDocumentNode<FollowingCountsQuery, FollowingCountsQueryVariables> = gql`
  query FollowingCountsQuery($profileId: String!) {
    followingCounts(filter: { profileId: $profileId }) {
      followers
      following
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId/following')({
  component: Layout,
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, FOLLOWING_COUNTS_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function Layout() {
  const { queryRef } = Route.useLoaderData()
  const { profileId } = Route.useParams()
  const { data } = useReadQuery(queryRef as QueryRef<FollowingCountsQuery>)
  const location = useLocation()
  const pathname = location.pathname

  const isFollowersActive = pathname.includes('/followers')
  const isFollowingActive = !isFollowersActive

  const followingCounts = data.followingCounts

  return (
    <>
      <div className="flex w-full flex-wrap items-center justify-center gap-2 py-2">
        <Button
          nativeButton={false}
          render={<Link className="no-underline" params={{ profileId } as never} to="/profile/$profileId/following" />}
          size="sm"
          variant={isFollowingActive ? 'pill' : 'pill-outline'}
        >
          Following
          <Badge className="min-h-5 min-w-5 px-1" size="sm" variant="secondary">
            {followingCounts?.following ?? 0}
          </Badge>
        </Button>
        <Button
          nativeButton={false}
          render={
            <Link
              className="no-underline"
              params={{ profileId } as never}
              to="/profile/$profileId/following/followers"
            />
          }
          size="sm"
          variant={isFollowersActive ? 'pill' : 'pill-outline'}
        >
          Followers
          {followingCounts && followingCounts.followers > 0 && (
            <Badge className="min-h-5 min-w-5 px-1" size="sm" variant={isFollowersActive ? 'secondary' : 'default'}>
              {followingCounts.followers}
            </Badge>
          )}
        </Button>
      </div>
      <Outlet />
    </>
  )
}
